import {
  Button,
  Card,
  ModalProps,
  SkeletonLoader,
  Table,
  toast,
  Modal,
} from '@insight-health-ai/roseate/components';
import { useScreenSize } from '@insight-health-ai/roseate/utils';
import { useNavigate } from 'react-router-dom';
import { useMemo, useState } from 'react';
import { Divider, Drawer } from 'antd';
import {
  useGetCallInfiniteList,
  UseGetCallListParams,
  useCallListClient,
} from 'src/hooks/call';
import { Call } from 'src/models';
import {
  CallPriorityDropdown,
  CallStatusDropdown,
  PractitionerDropdown,
  TagDropdown,
} from 'src/components';
import {
  getColumns,
  defaultFilters,
  sortDirectionMap,
  UnreadIcon,
  hasFiltersApplied,
} from './constants';
import { SorterResult } from 'antd/es/table/interface';
import { usePageHeader } from 'src/hooks/misc';
import { CallDetails } from '../details/callDetails';
import { useTagEncounter, useUntagEncounter } from 'src/hooks/encounter';
import classNames from 'classnames';
import { usePermissionEntitlements } from '@frontegg/react';
import {
  useCallBulkArchive,
  useCallBulkProcess,
  useCallBulkRead,
} from 'src/hooks/call/useCallBulkAction';
import styled from '@emotion/styled';
import { FilterOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import { DateRangePicker } from './dateRangePicker';

const StyledCard = styled(Card)`
  height: 100%;
  .ant-card-head {
    flex-shrink: 0;
  }
`;

const getDefaultFilters = () => {
  try {
    const filters = new URLSearchParams(window.location.search).get('filters');
    return {
      ...defaultFilters,
      ...(filters ? JSON.parse(filters) : {}),
    };
  } catch (error) {
    return defaultFilters;
  }
};

export const CallList = () => {
  const { addTagToCall, removeTagFromCall } = useCallListClient();
  const navigate = useNavigate();
  const [callPreview, setCallPreview] = useState<Call | null>(null);
  const [filters, setFilters] = useState<UseGetCallListParams>(() =>
    getDefaultFilters()
  );
  const [selectedCalls, setSelectedCalls] = useState<Call['call_id'][]>([]);
  const { isEntitled: hasCallDeleteAccess } =
    usePermissionEntitlements('calls.delete');

  const { mediaMax } = useScreenSize();

  const {
    data: callsData,
    isLoading,
    error,
    hasNextPage,
    fetchNextPage,
    isFetchingNextPage,
    isFetching,
  } = useGetCallInfiniteList(filters);

  const { mutateAsync: tagEncounter } = useTagEncounter();
  const { mutateAsync: untagEncounter } = useUntagEncounter();
  const { mutateAsync: bulkRead } = useCallBulkRead();
  const { mutateAsync: bulkArchive } = useCallBulkArchive();
  const { mutateAsync: bulkProcess } = useCallBulkProcess();

  const applyFilters = (
    newFilters: Partial<typeof filters>,
    replace = false
  ) => {
    let updatedFilters = replace ? newFilters : { ...filters, ...newFilters };
    setFilters(updatedFilters);
    const params = new URLSearchParams();
    params.set('filters', JSON.stringify(updatedFilters));
    navigate(`/phone-triage/calls?${params.toString()}`);
  };

  const handleBulkAction = async (action: 'read' | 'archive' | 'processed') => {
    const title: Record<
      typeof action,
      {
        title: string;
        content: string;
        appearance: ModalProps['appearance'];
      }
    > = {
      read: {
        title: `Mark calls as read`,
        content: `Are you sure you want to mark selected (${selectedCalls.length}) calls as read?`,
        appearance: 'success',
      },
      archive: {
        title: `Archive calls`,
        content: `Are you sure you want to archive selected (${selectedCalls.length}) calls?`,
        appearance: 'error',
      },
      processed: {
        title: `Mark calls as processed`,
        content: `Are you sure you want to mark selected (${selectedCalls.length}) calls as processed?`,
        appearance: 'success',
      },
    };

    await Modal.confirm({
      ...title[action],
      size: 'sm',
      primaryAction: {
        label: 'Proceed',
        onClick: async () => {
          try {
            switch (action) {
              case 'read':
                await bulkRead({ callIds: selectedCalls, isRead: true });
                break;
              case 'archive':
                await bulkArchive({ callIds: selectedCalls });
                break;
              case 'processed':
                await bulkProcess({
                  callIds: selectedCalls,
                  processed: true,
                });
                break;
            }
          } catch (error) {
            toast.error('Failed to perform action');
            throw error;
          }
          setSelectedCalls([]);
        },
      },
    });
  };

  usePageHeader({
    title: 'Recent Calls',
  });

  if (error) {
    return (
      <div className="flex justify-center items-center p-8">
        <div className="text-red-500 text-lg">Error loading calls</div>
      </div>
    );
  }

  const data = useMemo(() => {
    return (
      callsData?.pages.flatMap((page) =>
        page.callList.map((call) => ({
          ...call,
          key: call.call_id,
        }))
      ) || []
    );
  }, [callsData]);

  return (
    <>
      <div
        style={{ height: 'calc(100dvh - 100px)' }}
        className="page-wrapper care-connect-page flex flex-col gap-2 h-full overflow-hidden css-reset"
      >
        {/* <CallsAnalytics /> */}

        <StyledCard
          padding={{ body: 'none', header: 'sm' }}
          title={
            <div className="flex items-center gap-3 py-2 font-normal flex-wrap">
              <FilterOutlined />
              <Button
                variant="outlined"
                appearance={filters.isRead === false ? 'primary' : 'secondary'}
                icon={{
                  icon: <UnreadIcon showTooltip={false} />,
                  position: 'start',
                }}
                shape="round"
                onClick={() => {
                  const { isRead, ...rest } = filters;
                  applyFilters(
                    filters.isRead === false
                      ? rest
                      : { ...rest, isRead: false },
                    true
                  );
                }}
              >
                Unread
              </Button>
              {hasFiltersApplied(filters) && (
                <span
                  className="text-sm cursor-pointer hover:text-primary hover:underline"
                  onClick={() => {
                    const {
                      sortBy: _,
                      sortDirection: __,
                      ...rest
                    } = defaultFilters;
                    const { sortBy, sortDirection } = filters;
                    applyFilters(
                      {
                        sortBy,
                        sortDirection,
                        ...rest,
                      },
                      true
                    );
                  }}
                >
                  Clear filters
                </span>
              )}
              <Divider
                type="vertical"
                style={{
                  height: '20px',
                  margin: '0',
                  borderWidth: '0.5px',
                }}
              />
              <TagDropdown
                value={filters.tagIds || []}
                onChange={(tagIds) => {
                  applyFilters({ tagIds: tagIds.length ? tagIds : undefined });
                }}
              />
              <CallStatusDropdown
                value={filters.statusIds || []}
                onChange={(statusIds) => {
                  applyFilters({
                    statusIds: statusIds.length ? statusIds : undefined,
                  });
                }}
              />
              <CallPriorityDropdown
                value={filters.priorityIds || []}
                onChange={(priorityIds) => {
                  applyFilters({
                    priorityIds: priorityIds.length ? priorityIds : undefined,
                  });
                }}
              />
              <PractitionerDropdown
                value={
                  filters.assignedToPractitionerIds
                    ? filters.assignedToPractitionerIds?.map(Number)
                    : undefined
                }
                showSearch
                placeholder="Assigned To"
                allowClear
                onChange={(value) => {
                  applyFilters({
                    assignedToPractitionerIds: value
                      ? [value.toString()]
                      : undefined,
                  });
                }}
              />
              <div style={{ width: 180 }}>
                <DateRangePicker
                  value={[
                    filters.startTimeGte ? dayjs(filters.startTimeGte) : null,
                    filters.startTimeLte ? dayjs(filters.startTimeLte) : null,
                  ]}
                  onChange={(value) => {
                    applyFilters({
                      startTimeGte: value?.[0]?.toISOString(),
                      startTimeLte: value?.[1]?.toISOString(),
                    });
                  }}
                />
              </div>
              {selectedCalls.length ? (
                <div className="ml-auto right-0 flex gap-2 flex-wrap">
                  <Button
                    variant="outlined"
                    appearance="secondary"
                    icon={{
                      icon: (
                        <div className="font-medium text-xs flex items-center justify-center">
                          ({selectedCalls.length})
                        </div>
                      ),
                      position: 'end',
                    }}
                    onClick={() => handleBulkAction('read')}
                  >
                    Mark Read
                  </Button>
                  {hasCallDeleteAccess && (
                    <Button
                      variant="outlined"
                      icon={{
                        icon: (
                          <div className="font-medium text-xs flex items-center justify-center">
                            ({selectedCalls.length})
                          </div>
                        ),
                        position: 'end',
                      }}
                      appearance="danger"
                      onClick={() => handleBulkAction('archive')}
                    >
                      Archive
                    </Button>
                  )}
                  <Button
                    appearance="primary"
                    icon={{
                      icon: (
                        <div className="font-medium text-xs flex items-center justify-center">
                          ({selectedCalls.length})
                        </div>
                      ),
                      position: 'end',
                    }}
                    onClick={() => handleBulkAction('processed')}
                  >
                    Process
                  </Button>
                </div>
              ) : null}
            </div>
          }
        >
          <Table
            columns={getColumns({
              filters,
              onTagChange: (record, tag, action) => {
                if (action === 'add') {
                  addTagToCall({
                    callId: record.call_id,
                    tag,
                  });
                  tagEncounter({
                    encounterId: record.encounter_id!,
                    tag,
                  }).catch(() => {
                    removeTagFromCall({
                      callId: record.call_id,
                      tag,
                    });
                  });
                } else {
                  removeTagFromCall({
                    callId: record.call_id,
                    tag,
                  });
                  untagEncounter({
                    encounterId: record.encounter_id!,
                    tag,
                  }).catch(() => {
                    addTagToCall({
                      callId: record.call_id,
                      tag,
                    });
                  });
                }
              },
            })}
            data={data}
            loading={isLoading}
            getRowProps={(record) => ({
              className: classNames('cursor-pointer', {
                'bg-yellow-50/50': !record.is_read,
              }),
              onDoubleClick: () =>
                navigate(`/phone-triage/calls/${record.call_id}`),
              onClick: () => {
                setCallPreview(record);
                if (!record.is_read) {
                  bulkRead({ callIds: [record.call_id], isRead: true });
                }
              },
            })}
            pagination={false}
            footer={() => {
              if (!data?.length) {
                return null;
              }

              return (
                <div className="flex flex-col p-3 gap-2 items-center justify-center">
                  {isFetching ? (
                    <SkeletonLoader
                      active
                      paragraph={{ rows: 1, width: 100 }}
                      title={false}
                      style={{ width: 100 }}
                    />
                  ) : (
                    <div className="text-sm">
                      Showing {data?.length} of{' '}
                      {callsData?.pages[0]?.total || 'many'}
                    </div>
                  )}
                  {hasNextPage && (
                    <Button
                      loading={isFetchingNextPage}
                      onClick={() => {
                        fetchNextPage();
                      }}
                    >
                      Show more
                    </Button>
                  )}
                </div>
              );
            }}
            emptyText="No calls found"
            rowSelection={{
              selectedRowKeys: selectedCalls,
              onChange: (selectedRowKeys) => {
                setSelectedCalls(selectedRowKeys as Call['call_id'][]);
              },
            }}
            onChange={(_, __, sorter) => {
              const sortConfig = sorter as SorterResult<Call>;
              applyFilters({
                sortBy: sortConfig?.columnKey as keyof typeof filters,
                sortDirection:
                  sortDirectionMap[
                    sortConfig?.order as keyof typeof sortDirectionMap
                  ] || undefined,
              });
            }}
          />
        </StyledCard>
      </div>
      {callPreview && (
        <Drawer
          styles={{
            body: {
              padding: '0.75rem',
            },
          }}
          title="Call Details"
          className="insight-background"
          open
          onClose={() => {
            setCallPreview(null);
          }}
          width={mediaMax('sm') ? '100%' : '90%'}
        >
          <CallDetails callId={callPreview?.call_id} />
        </Drawer>
      )}
    </>
  );
};
